import { FileOutlined, ToolOutlined } from '@ant-design/icons';
import { Collapse, Spin, Tag, Typography, theme } from 'antd';
import Markdown from 'react-markdown';

import type { ToolCallDto } from '@clh/api-client';

import humlaBotSrc from './humla-bot.png';
import type { ChatMessage } from './types';

const { Text } = Typography;

interface ChatMessagesProps {
    messages: ChatMessage[];
    isStreaming?: boolean;
}

function DocumentLink({ url, mimeType }: { url: string; mimeType?: string }) {
    const isPdf =
        mimeType === 'application/pdf' ||
        url.split('?')[0].toLowerCase().endsWith('.pdf');

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 10px',
                background: '#f5f5f5',
                borderRadius: 6,
                border: '1px solid #d9d9d9',
                textDecoration: 'none',
                color: '#1677ff',
                fontSize: 12,
                marginTop: 4,
            }}
        >
            <FileOutlined />
            {isPdf ? 'View PDF' : 'View document'}
        </a>
    );
}

function AttachmentChip({ name }: { name: string }) {
    return (
        <div
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 10px',
                background: 'rgba(255,255,255,0.2)',
                borderRadius: 6,
                fontSize: 12,
            }}
        >
            <FileOutlined />
            {name}
        </div>
    );
}

function UserAttachments({
    attachments,
}: {
    attachments: ChatMessage['attachments'];
}) {
    if (!attachments?.length) return null;

    return (
        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                marginTop: 8,
            }}
        >
            {attachments.map((att) => {
                if (att.mimeType === 'application/pdf' || !att.url) {
                    return <AttachmentChip key={att.name} name={att.name} />;
                }

                return (
                    <img
                        key={att.name}
                        src={att.url}
                        alt={att.name}
                        onClick={() =>
                            att.url && window.open(att.url, '_blank')
                        }
                        style={{
                            maxWidth: 150,
                            maxHeight: 100,
                            borderRadius: 6,
                            border: '1px solid rgba(255,255,255,0.3)',
                            cursor: 'pointer',
                        }}
                    />
                );
            })}
        </div>
    );
}

function getToolCallSubtitle(tc: ToolCallDto): string | null {
    const input = tc.input as Record<string, unknown> | undefined;
    if (!input) return null;

    switch (tc.name) {
        case 'humla_api_call':
            return typeof input.name === 'string' ? input.name : null;
        case 'humla_api_info':
            return Array.isArray(input.names) ? input.names.join(', ') : null;
        case 'run_sql_query':
            if (typeof input.query === 'string') {
                const q = input.query.replace(/\s+/g, ' ').trim();
                return q.length > 80 ? `${q.slice(0, 77)}…` : q;
            }
            return null;
        case 'run_nrql_query':
            if (typeof input.query === 'string') {
                const q = input.query.replace(/\s+/g, ' ').trim();
                return q.length > 80 ? `${q.slice(0, 77)}…` : q;
            }
            return null;
        default:
            return null;
    }
}

function ToolCallResult({ tc }: { tc: ToolCallDto }) {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
            }}
        >
            <div>
                <Text
                    type="secondary"
                    style={{
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                    }}
                >
                    Input
                </Text>
                <pre
                    style={{
                        fontSize: 12,
                        maxHeight: 150,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        background: '#f5f5f5',
                        padding: 8,
                        borderRadius: 4,
                        margin: '4px 0 0',
                    }}
                >
                    {JSON.stringify(tc.input, null, 2)}
                </pre>
            </div>
            {tc.result !== undefined && (
                <div>
                    <Text
                        type="secondary"
                        style={{
                            fontSize: 11,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                        }}
                    >
                        Result
                    </Text>
                    <pre
                        style={{
                            fontSize: 12,
                            maxHeight: 250,
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            background: '#f5f5f5',
                            padding: 8,
                            borderRadius: 4,
                            margin: '4px 0 0',
                        }}
                    >
                        {tc.result}
                    </pre>
                </div>
            )}
            {tc.imageUrl && (
                <DocumentLink url={tc.imageUrl} mimeType={tc.imageMimeType} />
            )}
        </div>
    );
}

export function ChatMessages({
    messages,
    isStreaming = false,
}: ChatMessagesProps) {
    const {
        token: { colorBgContainer, borderRadiusLG, colorPrimary },
    } = theme.useToken();

    return (
        <>
            {messages.map((msg, idx) => (
                <div
                    key={msg.id}
                    style={{
                        display: 'flex',
                        justifyContent:
                            msg.role === 'user' ? 'flex-end' : 'flex-start',
                        marginBottom: 16,
                    }}
                >
                    {msg.role === 'user' ? (
                        <div style={{ maxWidth: '75%' }}>
                            {msg.profile && (
                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize: 12,
                                        display: 'block',
                                        textAlign: 'right',
                                        marginBottom: 4,
                                    }}
                                >
                                    {msg.profile.firstName}{' '}
                                    {msg.profile.lastName}
                                </Text>
                            )}
                            <div
                                style={{
                                    padding: '10px 16px',
                                    borderRadius: borderRadiusLG,
                                    background: colorPrimary,
                                    color: '#fff',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.6,
                                }}
                            >
                                {msg.content}
                                <UserAttachments
                                    attachments={msg.attachments}
                                />
                            </div>
                        </div>
                    ) : (
                        <div
                            style={{
                                maxWidth: '85%',
                                minWidth: 0,
                                display: 'flex',
                                gap: 10,
                            }}
                        >
                            <img
                                src={humlaBotSrc}
                                alt="Humla Bot"
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    marginTop: 4,
                                }}
                            />
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 8,
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: 'hidden',
                                }}
                            >
                                {/* Tool calls */}
                                {msg.toolCalls && msg.toolCalls.length > 0 && (
                                    <Collapse
                                        size="small"
                                        style={{
                                            background: colorBgContainer,
                                        }}
                                        items={msg.toolCalls.map((tc) => ({
                                            key: tc.id,
                                            label: (() => {
                                                const subtitle =
                                                    getToolCallSubtitle(tc);
                                                return (
                                                    <span
                                                        style={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            gap: 6,
                                                        }}
                                                    >
                                                        <ToolOutlined
                                                            style={{
                                                                fontSize: 12,
                                                                color: '#888',
                                                            }}
                                                        />
                                                        <code
                                                            style={{
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            {tc.name}
                                                        </code>
                                                        {subtitle && (
                                                            <Text
                                                                type="secondary"
                                                                style={{
                                                                    fontSize: 11,
                                                                }}
                                                                ellipsis
                                                            >
                                                                — {subtitle}
                                                            </Text>
                                                        )}
                                                        {tc.result !==
                                                        undefined ? (
                                                            <Tag
                                                                color={
                                                                    tc.result.startsWith(
                                                                        'Error:'
                                                                    )
                                                                        ? 'red'
                                                                        : 'green'
                                                                }
                                                                style={{
                                                                    fontSize: 11,
                                                                    lineHeight:
                                                                        '18px',
                                                                    marginLeft: 4,
                                                                }}
                                                            >
                                                                {tc.result.startsWith(
                                                                    'Error:'
                                                                )
                                                                    ? 'error'
                                                                    : 'done'}
                                                            </Tag>
                                                        ) : (
                                                            <Spin
                                                                size="small"
                                                                style={{
                                                                    marginLeft: 4,
                                                                }}
                                                            />
                                                        )}
                                                    </span>
                                                );
                                            })(),
                                            children: (
                                                <ToolCallResult tc={tc} />
                                            ),
                                        }))}
                                    />
                                )}

                                {/* Text content */}
                                {(msg.content ||
                                    !(
                                        isStreaming &&
                                        idx === messages.length - 1
                                    )) && (
                                    <div
                                        style={{
                                            padding: '10px 16px',
                                            borderRadius: borderRadiusLG,
                                            background: colorBgContainer,
                                            wordBreak: 'break-word',
                                            overflowX: 'auto',
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        <Markdown>{msg.content}</Markdown>
                                    </div>
                                )}

                                {/* Streaming indicator */}
                                {isStreaming &&
                                    idx === messages.length - 1 &&
                                    !msg.content && (
                                        <div
                                            style={{
                                                padding: '10px 16px',
                                                borderRadius: borderRadiusLG,
                                                background: colorBgContainer,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                            }}
                                        >
                                            <Spin size="small" />
                                            <Text type="secondary">
                                                {msg.toolCalls &&
                                                msg.toolCalls.length > 0
                                                    ? 'Processing...'
                                                    : 'Thinking...'}
                                            </Text>
                                        </div>
                                    )}
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </>
    );
}
